import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { FaCheck } from "react-icons/fa";

const Flux = () => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "module";
    script.src = "https://gradio.s3-us-west-2.amazonaws.com/4.40.0/gradio.js";
    script.onload = () => setLoading(false);
    document.body.appendChild(script);

    const timeout = setTimeout(() => setLoading(false), 5000);
    return () => clearTimeout(timeout);
  }, []);

  const features = [
    {
      title: "AI-Powered Transformation",
      description: "Experience the magic of AI as it transforms your photos with stunning new hairstyles in seconds."
    },
    {
      title: "Extensive Style Collection",
      description: "Choose from hundreds of trendy hairstyles for both men and women, or upload your own reference."
    },
    {
      title: "Realistic Results",
      description: "Our advanced AI ensures natural-looking results that match your facial features perfectly."
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "AI Hairstyle Generator - Try On Random Hairstyles",
    "description": "Generate AI-powered hairstyles from text prompts. Explore various haircuts and styles with our free hairstyle generator. Try different looks instantly!",
    "applicationCategory": "Beauty & Lifestyle",
  };

  return (
    <>
     <Helmet>
        <title>AI Hairstyle Generator - Try On Random Hairstyles</title>
        <meta name="description" content="Generate AI-powered hairstyles from text prompts. Explore various haircuts and styles with our free hairstyle generator. Try different looks instantly!" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-200 p-5 font-sans text-gray-800">
      <div className="bg-white shadow-2xl rounded-3xl p-10 max-w-4xl w-full text-center border border-gray-300">
        <h1 className="text-4xl text-teal-500 font-extrabold mb-6 animate-fade-in">
          Random Hairstyle Generator: AI Text to Image Creativity
        </h1>
        <img src="/images/tools/title.png" alt="Random Hairstyle Generator" className=" mx-auto animate-fade-in delay-100 " />
        <p className="text-lg text-gray-700 max-w-2xl mt-2 mx-auto mb-6 leading-relaxed">
          Welcome to the free AI Hairstyle Studio's Random Hairstyle Generator—a revolutionary, AI-powered tool that transforms your text prompts into stunning, high-quality images.
        </p>
        {loading ? (
          <div className="flex flex-col items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-teal-500"></div>
            <p className="mt-4 text-teal-600 font-semibold text-lg">Loading...</p>
          </div>
        ) : (
          <div className="w-full shadow-md rounded-lg overflow-hidden border border-gray-200">
            <gradio-app src="https://black-forest-labs-flux-1-schnell.hf.space"></gradio-app>
          </div>
        )}
        <div className="mt-10 text-left">
          <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-6 leading-relaxed">Leveraging the advanced capabilities of the FLUX engine, our <a className="text-teal-500 font-semibold underline cursor-pointer">AI hairstyle generator</a> offers more than just hairstyle visualizations; it opens up a world of creative possibilities. Whether you're a hairstylist seeking inspiration, a fashion enthusiast exploring new trends, or simply curious about different looks, our tool provides an interactive and immersive experience.</p>
          <h2 className="text-3xl font-bold text-teal-500 mb-4">Key Features of Random Hairstyle Generator:</h2>
         <ul className="list-none space-y-3 text-gray-700 text-lg">
  <li className="flex items-start gap-2">
    <FaCheck className="text-teal-500 mt-1 shrink-0" />
    <span><strong>AI-Powered Image Generation:</strong> Transform descriptive text prompts into vivid, personalized images, allowing for endless creative exploration.</span>
  </li>
  <li className="flex items-start gap-2">
    <FaCheck className="text-teal-500 mt-1 shrink-0" />
    <span><strong>State-of-the-Art Performance:</strong> Experience the best of FLUX technology, ensuring rapid image generation without compromising on quality.</span>
  </li>
  <li className="flex items-start gap-2">
    <FaCheck className="text-teal-500 mt-1 shrink-0" />
    <span><strong>High-Quality Visuals:</strong> Enjoy exceptional image detail and clarity, making each generated image a realistic representation of your prompt.</span>
  </li>
  <li className="flex items-start gap-2">
    <FaCheck className="text-teal-500 mt-1 shrink-0" />
    <span><strong>Diverse Output:</strong> From classic hairstyles to avant-garde designs, explore a wide range of styles and concepts to suit any preference or inspiration.</span>
  </li>
  <li className="flex items-start gap-2">
    <FaCheck className="text-teal-500 mt-1 shrink-0" />
    <span><strong>User-Friendly Interface:</strong> Designed for both professionals and novices, our intuitive platform ensures a seamless and enjoyable user experience.</span>
  </li>
  <li className="flex items-start gap-2">
    <FaCheck className="text-teal-500 mt-1 shrink-0" />
    <span><strong>Free Access:</strong> Enjoy all features without any cost, making it accessible to everyone seeking creative inspiration.</span>
  </li>
</ul>
        </div>
        <div className="mt-10 text-left">
          <h2 className="text-3xl font-bold text-teal-500 mb-4">5 Best Hairstyle Prompts: When Looking For a New Look</h2>
          <p className="mb-4 text-lg text-gray-700 mt-2 ml-4">To kickstart your creative journey, here are some example prompts inspired by popular trends:</p>
          <ul className="list-disc list-inside space-y-4 text-lg">
            {[
              { name: "1. Sleek bob haircut, modern and stylish, in a professional studio setting.", link: "Bobcut" , short: "Bobcut" , image: "/images/tools/Bobcut.png"},
              { name: "2. Classic pompadour hairstyle, vintage yet trendy, captured in a barbershop ambiance.", link: "Crewcut" , short: "Crewcut" , image: "/images/tools/Crewcut.png"},
              { name: "3. Long, wavy hair with a beachy vibe, photographed at a seaside location.", link: "Beachy" , short: "Wavyhair" , image: "/images/tools/LongWavy.png"},
              { name: "4. Edgy pixie cut, bold and contemporary, featured in a high-fashion editorial shoot", link: "Pixie" , short: "Pixie" , image: "/images/tools/Pixie.png"},
              { name: "5. Intricate braids, stylish and cultural, set against a vibrant outdoor market backdrop.", link: "Two%20Braids" , short: "Two Braids" , image: "/images/tools/TwoBraids.png"}
            ].map((style, index) => (
              <li key={index} className="flex flex-col">
                <h3 className="text-teal-500 font-bold mt-4">{style.name}</h3>
                <img src={style.image} alt={style.short} className=" mx-auto animate-fade-in delay-100 mt-2 " />
                <a className="text-teal-400 hover:underline font-bold mt-2 text-center underline" href={`https://www.aihairstylestudio.com/studio-hair-salon/${style.link}`}>
                  What would I look like with {style.short} hairstyle? Try it on here
                </a>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-lg text-gray-700">These prompts serve as a starting point, and users are encouraged to craft their own descriptions to generate personalized images.</p>
        </div>
        <div className="mt-10">
          <h2 className="text-3xl font-bold text-teal-500 mb-4">Experience the Future of Image Generation:</h2>
          <p className="text-lg text-gray-700 leading-relaxed">
            With the Random Hairstyle Generator, the possibilities are limitless. Whether you're a professional seeking fresh ideas or someone curious about exploring new styles, our AI-powered tool offers a dynamic and interactive way to visualize concepts like never before.
          </p>
        </div>
      </div>
    </div>
    </>
  );
};

export default Flux;
